import React, { useEffect, useState, useCallback, useRef } from "react";
import { apiClient, ApiError } from "../lib/api";
import { AuthContext } from "./auth-context-types";

// Export AuthContext for testing
export { AuthContext };

interface UsageStats {
  links: { current: number; limit: number };
  qr_codes: { current: number; limit: number };
}

interface User {
  id: string;
  email: string;
  tier: string;
  usage?: UsageStats;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState<boolean>(false);

  // Session management constants
  const REGULAR_INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
  const REMEMBER_ME_INACTIVITY_TIMEOUT = 30 * 24 * 60 * 60 * 1000; // 30 days — matches server JWT expiry
  const SESSION_STORAGE_KEY = "go2url_session";
  const TOKEN_STORAGE_KEY = "go2url_token";

  // Refs for timeout management
  const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const clearError = () => setError(null);

  const isAuthenticated = !!user && !!token;

  // Session management functions
  const saveSession = useCallback(
    (token: string, user: User, rememberMe = false) => {
      const sessionData = {
        token,
        user,
        lastActivity: Date.now(),
        rememberMe,
      };

      if (typeof localStorage !== "undefined") {
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
      }
    },
    [],
  );

  const clearSession = useCallback(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(SESSION_STORAGE_KEY);
      localStorage.removeItem("go2url_remember_me"); // Clean up old remember me flag
    }
    apiClient.clearToken();
    setToken(null);
    setUser(null);
    setError(null);
    setRememberMe(false);

    // Clear inactivity timer
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = null;
    }
  }, []);

  const isSessionExpired = useCallback(
    (lastActivity: number, isRememberMe = false): boolean => {
      const timeout = isRememberMe
        ? REMEMBER_ME_INACTIVITY_TIMEOUT
        : REGULAR_INACTIVITY_TIMEOUT;
      return Date.now() - lastActivity > timeout;
    },
    [REGULAR_INACTIVITY_TIMEOUT, REMEMBER_ME_INACTIVITY_TIMEOUT],
  );

  const startInactivityTimer = useCallback(
    (isRememberMe?: boolean) => {
      // Clear existing timer
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
        inactivityTimer.current = null;
      }

      // Use parameter if provided, otherwise fall back to state
      const effectiveRememberMe =
        isRememberMe !== undefined ? isRememberMe : rememberMe;
      const timeout = effectiveRememberMe
        ? REMEMBER_ME_INACTIVITY_TIMEOUT
        : REGULAR_INACTIVITY_TIMEOUT;

      if (timeout <= 0) {
        return;
      }

      // Start new timer
      inactivityTimer.current = setTimeout(() => {
        clearSession();
      }, timeout);
    },
    [
      rememberMe,
      REGULAR_INACTIVITY_TIMEOUT,
      REMEMBER_ME_INACTIVITY_TIMEOUT,
      clearSession,
    ],
  );

  const refreshActivity = useCallback(() => {
    lastActivityRef.current = Date.now();

    // Get the current rememberMe value from localStorage to avoid race conditions with React state
    let currentRememberMe = rememberMe;
    if (typeof localStorage !== "undefined") {
      const storedSession = localStorage.getItem(SESSION_STORAGE_KEY);
      if (storedSession) {
        try {
          const sessionData = JSON.parse(storedSession);
          currentRememberMe = sessionData.rememberMe || false;
        } catch {
          // If parsing fails, fall back to React state
          currentRememberMe = rememberMe;
        }
      }
    }

    // Update session storage with latest activity
    if (typeof localStorage !== "undefined" && token && user) {
      const sessionData = {
        token,
        user,
        lastActivity: lastActivityRef.current,
        rememberMe: currentRememberMe,
      };
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    }

    // Restart inactivity timer with explicit rememberMe value from session data
    if (isAuthenticated) {
      startInactivityTimer(currentRememberMe);
    }
  }, [token, user, rememberMe, isAuthenticated, startInactivityTimer]);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken =
          typeof localStorage !== "undefined"
            ? localStorage.getItem(TOKEN_STORAGE_KEY)
            : null;
        const storedSession =
          typeof localStorage !== "undefined"
            ? localStorage.getItem(SESSION_STORAGE_KEY)
            : null;

        if (storedToken && storedSession) {
          const sessionData = JSON.parse(storedSession);
          const isRememberMeSession = sessionData.rememberMe || false;

          // Check if session is expired using appropriate timeout
          if (isSessionExpired(sessionData.lastActivity, isRememberMeSession)) {
            console.log("Session expired, clearing stored data");
            clearSession();
            setIsLoading(false);
            return;
          }

          try {
            // Validate token with server
            apiClient.setToken(storedToken);
            const response = await apiClient.getUserInfo();

            // Session is valid, restore state
            setUser(response.user);
            setToken(storedToken);
            setRememberMe(isRememberMeSession);
            lastActivityRef.current = sessionData.lastActivity;

            // Calculate remaining time and start timer
            const elapsed = Date.now() - sessionData.lastActivity;
            const timeout = isRememberMeSession
              ? REMEMBER_ME_INACTIVITY_TIMEOUT
              : REGULAR_INACTIVITY_TIMEOUT;
            const remaining = Math.max(0, timeout - elapsed);

            console.log(
              `Session restoration: ${isRememberMeSession ? "Remember Me" : "Regular"} session, elapsed: ${Math.round(elapsed / 1000)}s, remaining: ${Math.round(remaining / 1000)}s`,
            );

            if (remaining > 0) {
              inactivityTimer.current = setTimeout(() => {
                console.log("Session expired due to inactivity");
                clearSession();
              }, remaining);
            } else {
              // Should not happen due to isSessionExpired check, but safety net
              console.log("Session should have expired - clearing");
              clearSession();
            }
          } catch {
            console.log("Token validation failed, clearing session");
            clearSession();
          }
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        clearSession();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [
    isSessionExpired,
    clearSession,
    REGULAR_INACTIVITY_TIMEOUT,
    REMEMBER_ME_INACTIVITY_TIMEOUT,
  ]);

  const login = async (email: string, password: string, rememberMe = false) => {
    try {
      setError(null);
      setIsLoading(true);

      console.log(`Login attempt with rememberMe: ${rememberMe}`);
      const response = await apiClient.login(email, password, rememberMe);

      // Set up session
      apiClient.setToken(response.token);
      setToken(response.token);
      setUser(response.user);
      const effectiveRememberMe = response.rememberMe || rememberMe;
      console.log(
        `Login successful, effective rememberMe: ${effectiveRememberMe}`,
      );
      setRememberMe(effectiveRememberMe);
      saveSession(response.token, response.user, effectiveRememberMe);

      // Start inactivity timer with explicit remember me value
      startInactivityTimer(effectiveRememberMe);
    } catch (error) {
      if (
        error instanceof ApiError ||
        (error &&
          typeof error === "object" &&
          "name" in error &&
          error.name === "ApiError")
      ) {
        const apiError = error as ApiError;
        if (apiError.isEmailNotVerified) {
          setError(
            "Please verify your email before logging in. Check your inbox for a verification email.",
          );
          // Include email in error data for resend functionality
          (error as any).email = apiError.data?.email;
        } else {
          setError(apiError.message);
        }
      } else {
        setError("An unexpected error occurred");
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string) => {
    try {
      setError(null);
      setIsLoading(true);

      const response = await apiClient.register(email, password);

      // Registration successful, but no token returned (email verification required)
      // Return the response so the UI can show appropriate message
      return response;
    } catch (error) {
      if (
        error instanceof ApiError ||
        (error &&
          typeof error === "object" &&
          "name" in error &&
          error.name === "ApiError")
      ) {
        setError((error as any).message);
      } else {
        setError("An unexpected error occurred");
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const googleAuth = async () => {
    try {
      setError(null);
      setIsLoading(true);

      const response = await apiClient.googleAuth();

      // Set up session
      apiClient.setToken(response.token);
      setToken(response.token);
      setUser(response.user);
      saveSession(response.token, response.user);

      // Start inactivity timer
      startInactivityTimer();
    } catch (error) {
      if (
        error instanceof ApiError ||
        (error &&
          typeof error === "object" &&
          "name" in error &&
          error.name === "ApiError")
      ) {
        if ((error as any).status === 501) {
          setError(
            "Google OAuth is not yet available. Please use email/password login.",
          );
        } else {
          setError((error as any).message);
        }
      } else {
        setError("An unexpected error occurred");
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    apiClient.logout();
    clearSession();
  };

  const requestPasswordReset = async (email: string) => {
    try {
      setError(null);
      setIsLoading(true);

      const response = await apiClient.requestPasswordReset(email);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred");
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (token: string, password: string) => {
    try {
      setError(null);
      setIsLoading(true);

      const response = await apiClient.resetPassword(token, password);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred");
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    token,
    rememberMe,
    isLoading,
    isAuthenticated,
    login,
    register,
    googleAuth,
    logout,
    error,
    clearError,
    refreshActivity,
    requestPasswordReset,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
